import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAuth } from '@hooks/useAuth';
import { updateToken, userLogout } from '@redux/authent/authent.reducer';
import { RootState } from '@redux/store';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { createErrorMessage, isFirstConnectionExceptionPayload, isSyriusExceptionPayload } from '@utils/common.utils';
import { Mutex } from 'async-mutex';

import { AUTHENT_URL } from './authent/authent.api';

// create a new mutex
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: '/',
  fetchFn: window.__nativeFetch,
  prepareHeaders: (headers, { getState }) => {
    const authent = (getState() as RootState).authent;
    if (authent.token && authent.renewalToken) {
      headers.set('authorization', authent.token);
      headers.set('renewal', authent.renewalToken);
    }
    if (authent.activeUser.id !== -1) {
      headers.set('userName', authent.activeUser.login);
    }
    headers.set('Content-type', 'application/json; charset=utf-8');
    return headers;
  },
});
export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) {
    return result;
  }

  //First connection, not a real 401
  if (isFirstConnectionExceptionPayload(result.error)) {
    return result;
  }

  if (isSyriusExceptionPayload(result.error) && result.error.data.code !== 'authent.invalidToken') {
    api.dispatch(userLogout());
    return result;
  }

  if (mutex.isLocked()) {
    await mutex.waitForUnlock();
    return useAuth() ? baseQuery(args, api, extraOptions) : result;
  }

  // checking whether the mutex is locked
  const release = await mutex.acquire();
  try {
    const refreshResult = await baseQuery(
      { url: `${AUTHENT_URL}/token/renew`, method: 'POST', credentials: 'same-origin' },
      api,
      extraOptions,
    );

    const response = refreshResult.meta?.response;
    const newToken = response?.headers.get('Authorization') ?? null;

    if (!refreshResult.error && response?.ok && newToken) {
      api.dispatch(updateToken(newToken));
      result = await baseQuery(args, api, extraOptions);
    } else {
      refreshResult.error && createToast(createErrorMessage(refreshResult.error), ToastStatusEnum.ERROR);
      api.dispatch(userLogout());
    }
  } finally {
    release();
  }

  return result;
};
